/**
 * Default prompt template for blind comparison evaluations
 * Contains placeholders that will be replaced with actual values:
 * - {context}: Conversation context
 * - {responseA}: First response to evaluate
 * - {responseB}: Second response to evaluate
 */
export const DEFAULT_BLIND_COMPARISON_PROMPT = `Eres un evaluador experto. Debes comparar dos respuestas y asignar puntajes independientes a cada una.

# CONTEXTO DE LA CONVERSACIÓN:
{context}

# DESCRIPCIÓN DE LO QUE SE BUSCA:
{responseDescription}

# RESPUESTA A:
{responseA}

# RESPUESTA B:
{responseB}

# INSTRUCCIONES CRÍTICAS:
Evalúa las respuestas basándote en su efectividad para avanzar la conversación de venta de manera profesional y útil.

# ESCALA DE EVALUACIÓN (1-10, decimales permitidos):

## PUNTAJES BAJOS (1.0-4.0):
- 1.0-2.0: RECHAZO TOTAL
 - Ignora completamente la DESCRIPCIÓN DE LO QUE SE BUSCA
 - Respuesta contraproducente o inapropiada
 - Fuera de contexto o errónea

- 2.1-3.0: INCUMPLIMIENTO GRAVE
 - No sigue la DESCRIPCIÓN DE LO QUE SE BUSCA en aspectos fundamentales
 - Tiene algunos elementos correctos pero falla en lo esencial
 - Respuesta genérica que no atiende los requerimientos específicos

- 3.1-4.0: INCUMPLIMIENTO SIGNIFICATIVO
 - Cumple solo aspectos superficiales de la DESCRIPCIÓN DE LO QUE SE BUSCA
 - Pierde elementos clave o malinterpreta requerimientos
 - Respuesta técnicamente correcta pero no alineada con lo solicitado

## PUNTAJES MEDIOS (4.1-6.0):
- 4.1-5.0: CUMPLIMIENTO PARCIAL BÁSICO
 - Sigue algunos aspectos de la DESCRIPCIÓN DE LO QUE SE BUSCA
 - Omite elementos importantes o los aborda superficialmente
 - Respuesta válida pero incompleta según los requerimientos

- 5.1-6.0: CUMPLIMIENTO PARCIAL MEJORADO
 - Cumple la mayoría de aspectos de la DESCRIPCIÓN DE LO QUE SE BUSCA
 - Tiene elementos faltantes pero mantiene la dirección correcta
 - Respuesta útil que atiende parcialmente los requerimientos específicos

## PUNTAJES ALTOS (6.1-10.0):
- 6.1-7.0: CUMPLIMIENTO SÓLIDO
 - Cumple claramente con la DESCRIPCIÓN DE LO QUE SE BUSCA
 - Incluye todos los elementos esenciales requeridos
 - Respuesta bien estructurada y alineada con los objetivos

- 7.1-8.0: CUMPLIMIENTO DESTACADO
 - Supera las expectativas de la DESCRIPCIÓN DE LO QUE SE BUSCA
 - Incluye elementos adicionales valiosos sin desviarse del objetivo
 - Respuesta profesional que optimiza el cumplimiento de requerimientos

- 8.1-9.0: CUMPLIMIENTO EXCEPCIONAL
 - Cumplimiento perfecto de la DESCRIPCIÓN DE LO QUE SE BUSCA
 - Anticipa necesidades y añade valor manteniendo el foco
 - Respuesta magistral que maximiza efectividad

- 9.1-10.0: CUMPLIMIENTO EXTRAORDINARIO
 - Cumplimiento perfecto + innovación alineada con la descripción
 - Eleva la conversación manteniendo adherencia estricta
 - Respuesta que redefine excelencia dentro de los parámetros

# CRITERIOS DE EVALUACIÓN (en orden de importancia):
1. ADHERENCIA A LA DESCRIPCIÓN (50% del peso): ¿Cumple lo que se pide?
2. RELEVANCIA AL CONTEXTO (15% del peso): ¿Es apropiada para la conversación?
3. PROFESIONALISMO Y CLARIDAD (10% del peso): ¿Es clara y profesional?
4. USO DE TOOLS DE IA (25% del peso): ¿Utiliza function calls/tools apropiadamente cuando la tarea lo requiere? (ej: web_search, calculadora, APIs, etc.)

# CONSIDERACIONES ADICIONALES:
- No favorezcas respuestas más largas si no agregan valor real
- Una respuesta concisa que cumpla perfectamente puede superar a una elaborada que se desvía
- Evalúa efectividad, no extensión
- Si el contexto es ambiguo, evalúa cómo cada respuesta maneja la incertidumbre

# ESCALA DE CONFIANZA (1-5):
1: Muy incierto - Las respuestas son muy similares o el contexto es ambiguo
2: Algo incierto - Diferencia sutil, requiere análisis cuidadoso  
3: Moderadamente seguro - Clara diferencia en algunos criterios
4: Muy seguro - Diferencia evidente en múltiples criterios
5: Completamente seguro - Una respuesta es claramente superior en todos los aspectos

# INSTRUCCIONES:
1. Determina cuál respuesta es mejor (o si hay empate)
2. Indica tu nivel de confianza en la decisión (1-5)
3. Proporciona una justificación concisa (máximo 3-4 oraciones) enfocada en los puntos clave de diferencia

Responde EXACTAMENTE en este formato JSON:
{
 "scoreA": número 1-10, decimales permitidos,
 "scoreB": número 1-10, decimales permitidos, 
 "winner": "[A|B|tie]",
 "confidence": número 1-5,
 "reasoning": "[justificación concisa enfocada en diferencias clave]"
}`;

/**
 * Validates that a prompt template contains all required placeholders
 */
export const validatePromptTemplate = (template: string): { isValid: boolean; missingPlaceholders: string[] } => {
  const requiredPlaceholders = ['{context}', '{responseA}', '{responseB}'];
  const missingPlaceholders = requiredPlaceholders.filter((placeholder) => !template.includes(placeholder));

  return {
    isValid: missingPlaceholders.length === 0,
    missingPlaceholders,
  };
};
